/**
 * Inline dialer notice for lib/dialerCountryGate (desktop GlobalDialer + mobile
 * dialer): "Verify ID" for a country the account isn't entitled to, "Start
 * plan" for an entitled foreign country on a trial, and a link to support when
 * even a verified account can't call there or the carrier has the country off.
 * A domestic trial destination is left to TrialDestinationNotice (#1537), which
 * already explains it.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheck, Sparkles } from 'lucide-react';
import { useDirectUpgrade } from '@/components/billing/DirectUpgradeSheet';
import { apiRequest } from '@/lib/queryClient';
import { startIdentityVerification, type CountryGate } from '@/lib/dialerCountryGate';
import { supportHref, unblockLabel } from '@/lib/dialBlock';
import { NOTICE_BUTTON_CLASS, NOTICE_CLASS, SupportAlternative, SupportButton } from './DialBlockNotice';

/** Account-level KYC state (GET /api/account/kyc); verified → KYC can't unlock more. */
export function useAccountKycVerified(): boolean {
  const { data } = useQuery<{ status?: string }>({ queryKey: ['/api/account/kyc'], staleTime: 5 * 60 * 1000, retry: false });
  return data?.status === 'verified';
}

const NO_COUNTRIES: string[] = [];

/** Countries off in the carrier's geographic permissions. Unknown → none: the refusal is reported at call time. */
export function useCarrierDisabledCountries(): string[] {
  const { data } = useQuery<{ carrierDisabledVoice?: string[] }>({
    queryKey: ['/api/capabilities/outbound-countries'],
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  return data?.carrierDisabledVoice ?? NO_COUNTRIES;
}

export function CountryGateNotice({ gate, source }: { gate: CountryGate | null; source: string }) {
  const { available, openDirectUpgrade } = useDirectUpgrade();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (!gate || gate.kind === 'allowed' || (gate.kind === 'trial' && gate.domestic)) return null;

  const verify = async () => {
    setBusy(true);
    setErr(null);
    const r = await startIdentityVerification((url, body) => apiRequest('POST', url, body));
    setBusy(false);
    if (r.url) window.location.href = r.url;
    else setErr(r.error || 'Could not start verification.');
  };
  const startPlan = () => {
    if (available) openDirectUpgrade({ feature: 'voice', source });
    else window.location.href = '/settings/billing';
  };
  const btn = NOTICE_BUTTON_CLASS;
  // The ticket gets the country, its code and why; never the number dialled.
  const unblockHref = gate.kind === 'unavailable' || gate.kind === 'kyc'
    ? supportHref({ code: gate.kind === 'unavailable' ? gate.reason : 'country_not_enabled', country: gate.iso })
    : '';
  const unblock = gate.kind === 'unavailable' || gate.kind === 'kyc' ? unblockLabel(gate.countryName) : '';

  return (
    <div
      className={NOTICE_CLASS}
      role="status"
      data-testid={`country-gate-${gate.kind}`}
    >
      <p className="leading-snug">{gate.message}</p>
      {gate.kind === 'kyc' && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <button type="button" onClick={verify} disabled={busy} className={btn} data-testid="button-country-gate-verify">
            <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
            {busy ? 'Opening…' : 'Verify ID'}
          </button>
          <SupportAlternative label="Contact support" href={unblockHref} />
          {err && <span className="text-destructive">{err}</span>}
        </div>
      )}
      {gate.kind === 'unavailable' && (
        <div className="mt-2">
          <SupportButton label={unblock} href={unblockHref} />
        </div>
      )}
      {gate.kind === 'trial' && (
        <div className="mt-2">
          <button type="button" onClick={startPlan} className={btn} data-testid="button-country-gate-start-plan">
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
            Start plan
          </button>
        </div>
      )}
    </div>
  );
}
